import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Shield, Lock, Eye, CheckCircle, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubmitTipPage() {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    party: '',
    region: '',
    politician_name: '',
    contact_email: '',
    evidence_urls: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePartyChange = (value) => {
    setFormData(prev => ({ ...prev, party: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content || !formData.party) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        evidence_urls: formData.evidence_urls ? formData.evidence_urls.split('\n').filter(url => url.trim()) : []
      };
      
      const response = await axios.post(`${API}/tips`, payload);
      setSubmitted(response.data);
      toast.success('Tip submitted securely');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit tip');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen terminal-bg flex items-center justify-center p-4" data-testid="tip-submitted">
        <div className="secure-terminal max-w-lg w-full text-center">
          <CheckCircle className="h-16 w-16 mx-auto mb-6 text-green-400" />
          <h1 className="text-2xl font-bold mb-4 text-green-400">Tip Received</h1>
          <p className="text-slate-400 mb-6">
            Your anonymous tip has been securely submitted. Save your reference code to check the status.
          </p>
          <div className="bg-slate-900 p-4 border border-green-400/30 mb-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Reference Code</p>
            <p className="text-2xl font-bold text-green-400 font-mono" data-testid="reference-code">
              {submitted.reference_code}
            </p>
          </div>
          <p className="text-xs text-slate-500 mb-6">
            Keep this code private. You can use it to check the status of your submission.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(null);
              setFormData({
                title: '',
                content: '',
                party: '',
                region: '',
                politician_name: '',
                contact_email: '',
                evidence_urls: ''
              });
            }}
            className="bg-green-400 text-black hover:bg-green-300"
            data-testid="submit-another"
          >
            Submit Another Tip
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen terminal-bg" data-testid="submit-tip-page">
      {/* Secure Header */}
      <div className="border-b border-slate-800 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="h-6 w-6 text-green-400" />
            <span className="font-mono text-green-400 text-sm uppercase tracking-wider">
              Secure Submission Portal
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-white font-light mb-4">
            Submit a Tip
          </h1>
          <p className="text-slate-400 max-w-2xl">
            Share information about political misconduct anonymously. Your identity is protected.
            All submissions are encrypted and handled with strict confidentiality.
          </p>
        </div>
      </div>

      {/* Security Info */}
      <div className="border-b border-slate-800 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <Lock className="h-5 w-5 text-green-400 mt-0.5" />
              <div>
                <p className="font-mono text-white text-sm">Anonymous</p>
                <p className="text-slate-500 text-xs">No login required</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-green-400 mt-0.5" />
              <div>
                <p className="font-mono text-white text-sm">Encrypted</p>
                <p className="text-slate-500 text-xs">End-to-end security</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Eye className="h-5 w-5 text-green-400 mt-0.5" />
              <div>
                <p className="font-mono text-white text-sm">Confidential</p>
                <p className="text-slate-500 text-xs">Reviewed by editors only</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="secure-terminal space-y-6" data-testid="tip-form">
            <div className="flex items-center space-x-2 text-green-400 text-xs mb-6">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-mono">SECURE TERMINAL v1.0</span>
            </div>

            {/* Title */}
            <div>
              <Label className="text-green-400 font-mono text-xs uppercase tracking-wider">
                Subject Line *
              </Label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Brief description of the issue..."
                className="mt-2 bg-slate-900 border-slate-700 text-green-400 placeholder:text-slate-600 font-mono"
                required
                data-testid="tip-title"
              />
            </div>

            {/* Party */}
            <div>
              <Label className="text-green-400 font-mono text-xs uppercase tracking-wider">
                Political Party *
              </Label>
              <Select value={formData.party} onValueChange={handlePartyChange} required>
                <SelectTrigger 
                  className="mt-2 bg-slate-900 border-slate-700 text-green-400 font-mono"
                  data-testid="tip-party"
                >
                  <SelectValue placeholder="Select party..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="labour" className="text-green-400 font-mono">Labour</SelectItem>
                  <SelectItem value="conservative" className="text-green-400 font-mono">Conservative</SelectItem>
                  <SelectItem value="libdem" className="text-green-400 font-mono">Liberal Democrats</SelectItem>
                  <SelectItem value="snp" className="text-green-400 font-mono">SNP</SelectItem>
                  <SelectItem value="reform" className="text-green-400 font-mono">Reform UK</SelectItem>
                  <SelectItem value="green" className="text-green-400 font-mono">Green</SelectItem>
                  <SelectItem value="plaid" className="text-green-400 font-mono">Plaid Cymru</SelectItem>
                  <SelectItem value="dup" className="text-green-400 font-mono">DUP</SelectItem>
                  <SelectItem value="sinnfein" className="text-green-400 font-mono">Sinn Féin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Politician Name */}
            <div>
              <Label className="text-green-400 font-mono text-xs uppercase tracking-wider">
                Politician / Councillor Name (Optional)
              </Label>
              <Input
                name="politician_name"
                value={formData.politician_name}
                onChange={handleChange}
                placeholder="Name of the individual..."
                className="mt-2 bg-slate-900 border-slate-700 text-green-400 placeholder:text-slate-600 font-mono"
                data-testid="tip-politician"
              />
            </div>

            {/* Region */}
            <div>
              <Label className="text-green-400 font-mono text-xs uppercase tracking-wider">
                Region / Constituency (Optional)
              </Label>
              <Input
                name="region"
                value={formData.region}
                onChange={handleChange}
                placeholder="e.g., Greater Manchester, Westminster..."
                className="mt-2 bg-slate-900 border-slate-700 text-green-400 placeholder:text-slate-600 font-mono"
                data-testid="tip-region"
              />
            </div>

            {/* Content */}
            <div>
              <Label className="text-green-400 font-mono text-xs uppercase tracking-wider">
                Details *
              </Label>
              <Textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Provide as much detail as possible..."
                className="mt-2 min-h-[200px] bg-slate-900 border-slate-700 text-green-400 placeholder:text-slate-600 font-mono resize-none"
                required
                data-testid="tip-content"
              />
            </div>

            {/* Evidence URLs */}
            <div>
              <Label className="text-green-400 font-mono text-xs uppercase tracking-wider">
                Evidence Links (Optional)
              </Label>
              <Textarea
                name="evidence_urls"
                value={formData.evidence_urls}
                onChange={handleChange}
                placeholder="Paste links to documents, articles, or other evidence (one per line)..."
                className="mt-2 min-h-[100px] bg-slate-900 border-slate-700 text-green-400 placeholder:text-slate-600 font-mono resize-none"
                data-testid="tip-evidence"
              />
            </div>

            {/* Contact Email */}
            <div>
              <Label className="text-green-400 font-mono text-xs uppercase tracking-wider">
                Contact Email (Optional)
              </Label>
              <Input
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="Only if you want us to contact you..."
                className="mt-2 bg-slate-900 border-slate-700 text-green-400 placeholder:text-slate-600 font-mono"
                data-testid="tip-email"
              />
              <p className="text-slate-600 text-xs mt-1 font-mono">
                Providing email is optional. Your anonymity is preserved if left blank.
              </p>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full bg-green-400 text-black hover:bg-green-300 font-mono uppercase tracking-wider py-6"
                data-testid="submit-tip-button"
              >
                {submitting ? 'Encrypting & Submitting...' : 'Submit Securely'}
              </Button>
            </div>

            <p className="text-slate-600 text-xs font-mono text-center">
              By submitting, you confirm this information is accurate to the best of your knowledge.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
